import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { IoSendSharp } from "react-icons/io5";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Footer from "./Footer";
import Header from "./Header";
import Scroll from "./Scroll";
import { useNavigate } from "react-router-dom";
import CrackersLoader from './CrackersLoader';
import Sticker from "./Sticker";
import MobileNavigation from "./MobileNavigation";
import "./home.css"; // global styles + floating WhatsApp

// Section components
import CarouselBanner from "./sections/CarouselBanner";
import MarqueeBar from "./sections/MarqueeBar";
import WelcomeSection from "./sections/WelcomeSection";
import CategoriesSection from "./sections/CategoriesSection";
import EstimateSection from "./sections/EstimateSection";
import GallerySection from "./sections/GallerySection";
import SafetySection from "./sections/SafetySection";
import BrandsSection from "./sections/BrandsSection";

const Home = () => {
  const [isChatBoxVisible, setChatBoxVisible] = useState(false);
  const [showDos, setShowDos] = useState(true);
  const [categories, setCategories] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [marqueeText, setMarqueeText] = useState("");
  const [message, setMessage] = useState("");
  const [homeData, setHomeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [gallery, setGallery] = useState([]);
  const [clientlogo, setClientLogo] = useState([]);
  const navigate = useNavigate();
  const observerRef = useRef(null);

  const toggleChatBox = () => setChatBoxVisible(!isChatBoxVisible);
  const handleMessageChange = (e) => setMessage(e.target.value);
  const sendMessage = () => {
    if (!whatsappNumber) return;
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace("+", "")}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch banners, marquee, categories, gallery, client logos
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}home.php`);
        const { body } = response.data;
        setCategories(body?.category_front || []);
        setCarouselImages(body?.banner || []);
        setMarqueeText(body?.scrollnew?.map(item => item.msg).join(" | ") || "");
        setGallery(body?.gallery || []);
        setClientLogo(body?.client_logo || []);
      } catch (error) {
        console.error("Error fetching home data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch home content & WhatsApp number
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}content.php`);
        const { body } = response.data;
        const { whatsapp_number } = body.contact;
        setHomeData(body.home);
        setWhatsappNumber(whatsapp_number);
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };
    fetchHomeData();
  }, []);

  // ---- Intersection Observer runs AFTER the page is loaded ----
  useEffect(() => {
    if (isLoading || !homeData) return; // wait until spinner is gone and content exists

    const timer = setTimeout(() => {
      // disconnect any previous observer (if any)
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              // optionally unobserve after animation to save resources
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
      );

      // now observe all elements with .fade-in-up
      document.querySelectorAll(".fade-in-up").forEach((el) => {
        observerRef.current?.observe(el);
      });
    }, 200); // small delay to be 100% sure the DOM has all sections

    return () => {
      clearTimeout(timer);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [isLoading, homeData]); // runs when loading finishes and content data arrives
  // ----------------------------------------------------------

  const handleCategoryClick = (category) => {
    navigate('/quick', { state: { selectedCategory: category.category_name } });
  };

  if (isLoading) return <CrackersLoader />;
  if (!homeData) return <p></p>;

  return (
    <div className="home-main-container">
      <Header />
      <CarouselBanner images={carouselImages} />
      <MarqueeBar text={marqueeText} />
      <WelcomeSection homeData={homeData} />
      <Sticker />
      <CategoriesSection categories={categories} onCategoryClick={handleCategoryClick} />
      <EstimateSection />
      <GallerySection images={gallery} />
      <SafetySection showDos={showDos} setShowDos={setShowDos} />
      <BrandsSection logos={clientlogo} />

      {/* Floating WhatsApp (UNCHANGED) */}
      <div className="floating-whatsapp-container">
        <a
          href={`https://wa.me/${whatsappNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          className="floating-whatsapp"
        >
          <FontAwesomeIcon icon={faWhatsapp} />
        </a>
        {isChatBoxVisible && (
          <div className="chat-box">
            <div className="wats-header">
              <p>My Crackers</p>
              <button className="close-button" onClick={toggleChatBox}>X</button>
            </div>
            <div className="wats-content">
              <p>Welcome To My Crackers</p>
            </div>
            <div className="chat-content">
              <textarea placeholder="Type your message here..." value={message} onChange={handleMessageChange}></textarea>
              <button onClick={sendMessage}><IoSendSharp /></button>
            </div>
          </div>
        )}
      </div>

      <Scroll />
      <Footer />
      <MobileNavigation />
    </div>
  );
};

export default Home;